// The top bar of the game frame (spec/dashboard.md section 3.3): the breadcrumbs, the unsaved cluster (Undo / Redo of
// section 4.2, the count opening the Changes panel of section 4.5, Save all of section 4.3, Discard all), the search, the
// clock and the theme toggle.
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Button, IconButton } from '../ui/Button';
import { Badge } from '../ui/Chip';
import { ChevronRight, Clock as ClockIcon, Moon, PanelLeftOpen, Redo2, Search, Sun, Undo2 } from '../ui/icons';
import { Popover } from '../ui/Popover';
import { formatInstant, useTimeZone, zoneLabel, type ZoneContext } from '../ui/time';
import { effectiveTheme, loadTheme, saveTheme, type ThemeChoice } from '../ui/theme';
import { TimeZonePicker } from '../ui/values';
import type { Workspace } from '../workspace/Workspace';
import { useWorkspaceVersion } from '../workspace/react';

export interface Crumb {
  label: string;
  to?: string;
}

export interface TopBarProps {
  crumbs: Crumb[];
  workspace?: Workspace | null;
  onSaveAll?: () => void;
  onDiscardAll?: () => void;
  /** the buttons of Ctrl+Z / Ctrl+Y (spec section 4.2) */
  onUndo?: () => void;
  onRedo?: () => void;
  onOpenChanges?: () => void;
  onPalette?: () => void;
  saving?: boolean;
  canWrite?: boolean;
  /** the permission Save All needs here (content_write on a branch; manage_environment / players_write on an environment page) */
  writePermission?: string;
  zoneCtx?: ZoneContext;
  /** when the navigation panel is hidden, the button that brings it back */
  panelCollapsed?: boolean;
  onShowPanel?: () => void;
  /** the extensions' puzzle menu (section 10.7), rendered before the clock */
  extensions?: import('react').ReactNode;
}

export function Clock({ ctx }: { ctx: ZoneContext }) {
  const [zone] = useTimeZone();
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <button type="button" className="btn btn-ghost btn-s clock" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" title="Display time zone">
        <ClockIcon aria-hidden="true" />
        <span>{formatInstant(now, zone, ctx).slice(11)}</span>
        <span className="muted">{zoneLabel(zone, ctx)}</span>
      </button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Display time zone" placement="bottom-end">
        <div className="stack">
          <label className="field-label" htmlFor="tz-picker">
            Show every date in
          </label>
          <TimeZonePicker ctx={ctx} id="tz-picker" />
          <span className="muted small">Production game time is UTC. Dev and stage may carry an offset for testing calendar content.</span>
        </div>
      </Popover>
    </>
  );
}

function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(() => loadTheme());
  const next: ThemeChoice = effectiveTheme(choice) === 'dark' ? 'light' : 'dark';
  return (
    <IconButton
      label={`Switch to the ${next} theme`}
      title={`Theme: ${choice}`}
      onClick={() => {
        saveTheme(next);
        setChoice(next);
      }}
    >
      {effectiveTheme(choice) === 'dark' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </IconButton>
  );
}

export function TopBar({ crumbs, workspace, onSaveAll, onDiscardAll, onUndo, onRedo, onOpenChanges, onPalette, saving, canWrite = true, writePermission = 'content_write', zoneCtx = {}, panelCollapsed, onShowPanel, extensions }: TopBarProps) {
  useWorkspaceVersion(workspace ?? null);
  const count = workspace?.changeCount ?? 0;
  // the cluster stays while something is redoable, so a redo is reachable after undoing everything (section 4.2)
  const showCluster = count > 0 || (workspace?.canRedo() ?? false);
  const undoWhat = workspace?.undoLabel() ?? null;
  const redoWhat = workspace?.redoLabel() ?? null;
  const undoText = `Undo${undoWhat ? ` ${undoWhat}` : ''} (Ctrl+Z)`;
  const redoText = `Redo${redoWhat ? ` ${redoWhat}` : ''} (Ctrl+Y)`;
  return (
    <header className="topbar">
      {panelCollapsed && onShowPanel && (
        <IconButton label="Show navigation" onClick={onShowPanel}>
          <PanelLeftOpen aria-hidden="true" />
        </IconButton>
      )}
      <nav className="breadcrumbs" aria-label="breadcrumb">
        {crumbs.map((c, i) => (
          <Fragment key={i}>
            {i > 0 && <ChevronRight aria-hidden="true" />}
            <span>{c.to && i < crumbs.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span className={i === crumbs.length - 1 ? 'current' : ''}>{c.label}</span>}</span>
          </Fragment>
        ))}
      </nav>
      <span className="spacer" />
      {workspace && (
        <div className="unsaved" data-testid="unsaved-cluster">
          {showCluster && (
            <>
              <IconButton label={undoText} onClick={onUndo} disabled={!workspace.canUndo() || saving}>
                <Undo2 aria-hidden="true" />
              </IconButton>
              <IconButton label={redoText} onClick={onRedo} disabled={!workspace.canRedo() || saving}>
                <Redo2 aria-hidden="true" />
              </IconButton>
            </>
          )}
          {count > 0 && (
            <>
              <button type="button" className="btn btn-ghost btn-s" onClick={onOpenChanges} aria-label={`${count} unsaved changes; open the Changes panel`}>
                <Badge tone="warning">{count} unsaved</Badge>
              </button>
              <Button size="s" variant="primary" onClick={onSaveAll} busy={saving} disabled={workspace.isProtected} missingPermission={canWrite ? null : writePermission} title={workspace.isProtected ? 'This branch is protected: changes land through merge requests.' : 'Save all (Ctrl+S)'}>
                Save all
              </Button>
              <Button size="s" variant="ghost" onClick={onDiscardAll} disabled={saving}>
                Discard all
              </Button>
            </>
          )}
        </div>
      )}
      {extensions}
      {onPalette && (
        <button type="button" className="topbar-search" onClick={onPalette} aria-label="Search and go to… (Ctrl+K)" title="Go to… (Ctrl+K)">
          <Search aria-hidden="true" />
          <span className="label">Search…</span>
          <span className="kbd" aria-hidden="true">
            Ctrl K
          </span>
        </button>
      )}
      <Clock ctx={zoneCtx} />
      <ThemeToggle />
    </header>
  );
}
